import {ChangeDetectionStrategy, Component, effect, Injectable, input, signal} from '@angular/core';
import {MatButton} from '@angular/material/button';
import {MatOption, MatSelect} from '@angular/material/select';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {TranslateModule} from '@ngx-translate/core';
import {AsyncPipe, JsonPipe} from '@angular/common'; // Import the pipe
import {BrukerId, EndringshendelseId, EndringshendelseModell, Endringstype, RevisjonsloggRessursClient, Sortering, SubEndringstype, SøkeresultatModell} from 'felles-ng2/tjenester/ressurser/generertNg2HttpKlient';
import {AvatarKomponent} from 'web-ng2/delt/komponenter/avatar/avatar';
import {StrengMalPipe} from 'felles-ng2/pipes/StrengMalPipe';
import {FormaterZonedDateTimePipe} from 'felles-ng2/pipes/FormaterZonedDateTimePipe';
import {FormaterZonedDateRelativTimePipe} from 'felles-ng2/pipes/FormaterZonedDateTimeRelativPipe';
import {IframeAutoHoydeKomponent} from "web-ng2/delt/komponenter/iframe-auto-hoyde/iframe-auto-hoyde";

@Component({
    selector: 'administrasjon-bruker-hendeseslogg',
    templateUrl: 'administrasjon-bruker-hendeseslogg.html',
    styleUrl: 'administrasjon-bruker-hendeseslogg.scss',
    imports: [
        AvatarKomponent,
        StrengMalPipe,
        FormaterZonedDateTimePipe,
        FormaterZonedDateRelativTimePipe,
        MatButton,
        MatFormField,
        MatLabel,
        MatSelect,
        MatOption,
        TranslateModule,
        AsyncPipe,
      JsonPipe,
        IframeAutoHoydeKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {'data-testid': 'administrasjon-bruker-hendeseslogg'}
})
export class AdministrasjonBrukerHendesesloggKomponent {
    static ajsSelector = 'administrasjonBrukerHendeseslogg';

    readonly brukerId = input.required<BrukerId>();
    readonly antall = input<number>(20);

    readonly sortering = signal<Sortering>(Sortering.synkende);
    readonly offset = signal<number>(0);
    readonly laster = signal<boolean>(false);
    readonly hendelser = signal<EndringshendelseModell[]>([]);
    readonly totalantall = signal<number>(0);
    readonly ekspanderteHendelser = signal<Set<EndringshendelseId>>(new Set());

    readonly SorteringEnum = Sortering;

    constructor(private avhengigheter: AdministrasjonBrukerHendesesloggAvhengigheter) {
        effect(() => {
            const id = this.brukerId();
            const sort = this.sortering();
            const off = this.offset();
            const ant = this.antall();
            if (id) {
                void this.lastHendelser(id, sort, ant, off);
            }
        });


    }

    erEpost(hendelse: EndringshendelseModell): boolean {
        return hendelse.endringstype === Endringstype.epost;
    }

    erEkspandert(hendelse: EndringshendelseModell): boolean {
        return this.ekspanderteHendelser().has(hendelse.id);
    }

    veksleEkspandert(hendelse: EndringshendelseModell) {
        this.ekspanderteHendelser.update(gjeldende => {
            const oppdatert = new Set(gjeldende);
            if (oppdatert.has(hendelse.id)) {
                oppdatert.delete(hendelse.id);
            } else {
                oppdatert.add(hendelse.id);
            }
            return oppdatert;
        });
    }

    endringstypeNavn(endringstype: Endringstype | string | null | undefined): string {
        if (endringstype === null || endringstype === undefined) return '';
        if (typeof endringstype === 'number') {
            return Endringstype[endringstype] ?? String(endringstype);
        }
        return String(endringstype);
    }

    subEndringstypeNavn(subEndringstype: SubEndringstype | string | null | undefined): string {
        if (subEndringstype === null || subEndringstype === undefined) return '';
        if (typeof subEndringstype === 'number') {
            return SubEndringstype[subEndringstype] ?? String(subEndringstype);
        }
        return String(subEndringstype);
    }

    byttSortering(nySortering: Sortering) {
        if (this.sortering() !== nySortering) {
            this.sortering.set(nySortering);
            this.offset.set(0);
        }
    }

    forrigeSide() {
        const nyOffset = Math.max(0, this.offset() - this.antall());
        this.offset.set(nyOffset);
    }

    nesteSide() {
        if (this.harNesteSide()) {
            this.offset.update(gjeldende => gjeldende + this.antall());
        }
    }

    harForrigeSide(): boolean {
        return this.offset() > 0;
    }

    harNesteSide(): boolean {
        return this.offset() + this.antall() < this.totalantall();
    }

    viserFra(): number {
        if (this.totalantall() === 0) return 0;
        return this.offset() + 1;
    }

    viserTil(): number {
        return Math.min(this.offset() + this.antall(), this.totalantall());
    }

    private async lastHendelser(brukerId: BrukerId, sortering: Sortering, antall: number, offset: number) {
        this.laster.set(true);
        try {
            const resultat = await this.avhengigheter.finnEndringerUtførtAvBruker(brukerId, sortering, antall, offset);
            if (brukerId === this.brukerId()) {
                this.hendelser.set(resultat.treff);
                this.totalantall.set(resultat.totalantall);
            }
        } finally {
            if (brukerId === this.brukerId()) {
                this.laster.set(false);
            }
        }
    }
}

@Injectable({providedIn: 'root'})
export class AdministrasjonBrukerHendesesloggAvhengigheter {
    constructor(private revisjonsloggRessurs: RevisjonsloggRessursClient) {}

    finnEndringerUtførtAvBruker(
        brukerId: BrukerId,
        sortering: Sortering = Sortering.synkende,
        antall: number = 20,
        offset: number = 0
    ): Promise<SøkeresultatModell<EndringshendelseModell>> {
        return this.revisjonsloggRessurs.finnEndringerUtførtAvBruker({brukerId, sortering, antall, offset}).somPromise();
    }
}
